/**
 * DV-PROJ-001: Assets Section Component
 *
 * Manages project assets including:
 * - Asset upload and management
 * - Asset type filtering
 * - Preview thumbnails
 * - Asset deletion
 */

import React, { useState } from 'react';
import {
  Plus,
  FileImage,
  Upload,
  MoreHorizontal,
  Edit2,
  Trash2,
  Download,
  FileText,
  Package,
  Image as ImageIcon,
  MonitorSpeaker
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  useAssetsQuery,
  useDeleteAssetMutation
} from '@/hooks/useProjectsQuery';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import { Skeleton } from '@/components/ui/LoadingSkeletons';
import UploadAssetModal from '../modals/UploadAssetModal';
import type { Project, Asset, AssetType } from '@/types/projects';

interface AssetsSectionProps {
  project: Project;
}

interface AssetCardProps {
  asset: Asset;
  onEdit: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
}

// Asset type configurations
const assetTypeConfig = {
  bottle: {
    icon: Package,
    label: 'Bottle',
    color: 'text-green-400'
  },
  screenshot: {
    icon: MonitorSpeaker,
    label: 'Screenshot',
    color: 'text-blue-400'
  },
  logo: {
    icon: ImageIcon,
    label: 'Logo',
    color: 'text-purple-400'
  },
  other: {
    icon: FileText,
    label: 'Other',
    color: 'text-white/60'
  }
} as const;

const AssetCard: React.FC<AssetCardProps> = ({ asset, onEdit, onDelete }) => {
  const [showActions, setShowActions] = useState(false);

  const typeConfig = assetTypeConfig[asset.type];
  const TypeIcon = typeConfig.icon;

  // Check if asset is an image for preview
  const isImage = asset.mimeType?.startsWith('image/');

  return (
    <div className="relative group">
      <HolographicCard glow="blue" intensity="subtle" className="h-full">
        <CardContent className="p-4">
          {/* Preview */}
          <div className="aspect-square bg-black/50 border border-white/10 rounded-lg mb-3 overflow-hidden relative">
            {isImage ? (
              <img
                src={asset.fileUrl}
                alt={asset.fileName || asset.id}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <TypeIcon className={cn('w-8 h-8', typeConfig.color)} />
              </div>
            )}

            {/* Type Badge */}
            <div className={cn(
              'absolute top-2 left-2 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1',
              'bg-black/70'
            )}>
              <span className={cn('text-xs font-mono', typeConfig.color)}>
                {typeConfig.label}
              </span>
            </div>

            {/* File Size Badge */}
            {asset.fileSize && (
              <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1">
                <span className="text-xs text-white/80 font-mono">
                  {formatFileSize(asset.fileSize)}
                </span>
              </div>
            )}
          </div>

          {/* Asset Info */}
          <div className="mb-3">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-sm font-medium text-white/90 truncate flex-1">
                {asset.fileName || `Asset ${asset.id.slice(0, 8)}`}
              </h3>

              {/* Actions Menu */}
              <div className="relative ml-2">
                <button
                  onClick={() => setShowActions(!showActions)}
                  className={cn(
                    'p-1 rounded opacity-0 group-hover:opacity-100 transition-all duration-300',
                    'hover:bg-white/10 text-white/60 hover:text-white/80',
                    showActions && 'opacity-100 bg-white/10'
                  )}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>

                {showActions && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowActions(false)}
                    />
                    <div className="absolute right-0 top-8 z-20 bg-black/90 backdrop-blur-xl border border-white/20 rounded-lg shadow-xl min-w-[140px]">
                      <div className="py-2">
                        <a
                          href={asset.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setShowActions(false)}
                          className="w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center gap-2 block"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </a>
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onEdit(asset);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <div className="border-t border-white/10 my-1" />
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onDelete(asset);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {asset.notes && (
              <p className="text-xs text-white/50 line-clamp-2">
                {asset.notes}
              </p>
            )}
          </div>
        </CardContent>
      </HolographicCard>
    </div>
  );
};

// Utility function to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

const AssetsSection: React.FC<AssetsSectionProps> = ({ project }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState<AssetType | null>(null);

  const { data: assetsData, isLoading } = useAssetsQuery(project.id, typeFilter || undefined);
  const deleteAssetMutation = useDeleteAssetMutation();

  const assets = assetsData?.assets || [];

  const handleEdit = (asset: Asset) => {
    // TODO: Implement edit modal
    console.log('Edit asset:', asset);
  };

  const handleDelete = async (asset: Asset) => {
    const fileName = asset.fileName || `Asset ${asset.id.slice(0, 8)}`;
    if (window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      try {
        await deleteAssetMutation.mutateAsync({
          projectId: project.id,
          assetId: asset.id
        });
      } catch (error) {
        console.error('Failed to delete asset:', error);
      }
    }
  };

  // Get asset counts by type
  const assetCounts = assets.reduce((counts, asset) => {
    counts[asset.type] = (counts[asset.type] || 0) + 1;
    return counts;
  }, {} as Record<AssetType, number>);

  return (
    <div>
      <HolographicCard glow="blue" intensity="medium">
        <CardHeader
          title="PROJECT ASSETS"
          subtitle={`${assets.length} assets`}
          icon={<FileImage className="w-5 h-5" />}
          actions={
            <div className="flex items-center gap-3">
              {/* Type Filter */}
              <div className="relative">
                <button
                  onClick={() => setTypeFilter(null)}
                  className={cn(
                    'px-3 py-1 text-xs border rounded transition-colors',
                    !typeFilter
                      ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                      : 'border-white/20 text-white/60 hover:bg-white/10'
                  )}
                >
                  All
                </button>
              </div>

              {(Object.keys(assetTypeConfig) as AssetType[]).map((type) => {
                const config = assetTypeConfig[type];
                const count = assetCounts[type] || 0;

                return (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                    className={cn(
                      'px-3 py-1 text-xs border rounded transition-colors flex items-center gap-1',
                      typeFilter === type
                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                        : 'border-white/20 text-white/60 hover:bg-white/10'
                    )}
                  >
                    <config.icon className="w-3 h-3" />
                    {config.label}
                    {count > 0 && (
                      <span className="ml-1 text-xs opacity-60">({count})</span>
                    )}
                  </button>
                );
              })}

              <button
                onClick={() => setShowUploadModal(true)}
                className="btn-future text-blue-400 border-blue-400/50 hover:bg-blue-400/10 text-sm ml-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload Asset
              </button>
            </div>
          }
        />

        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-square" />
                  <Skeleton className="h-4" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))}
            </div>
          ) : assets.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {assets.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileImage className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-light text-white/70 mb-2">
                {typeFilter ? `No ${assetTypeConfig[typeFilter].label}s` : 'No Assets Yet'}
              </h3>
              <p className="text-white/40 mb-6 max-w-md mx-auto">
                {typeFilter
                  ? `Upload ${assetTypeConfig[typeFilter].label.toLowerCase()}s to use in your content creation.`
                  : 'Upload assets like bottles, screenshots, logos, and other media to use in your pipeline executions.'
                }
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="btn-future text-blue-400 border-blue-400/50 hover:bg-blue-400/10"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload {typeFilter ? assetTypeConfig[typeFilter].label : 'First Asset'}
              </button>
            </div>
          )}
        </CardContent>
      </HolographicCard>

      {/* Upload Asset Modal */}
      <UploadAssetModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        project={project}
      />
    </div>
  );
};

export default AssetsSection;